import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { sendToastMessage } from '../../../actions/toast';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import ButtonScogoClosedOutlined from '../../../common/Buttons/ButtonScogoClosedOutlined';
import scogo_apis from '../../../scogo_apis';
import { buildConfigPayload, configToRows, emptyConfigRow } from './assetConfigForm';
import { closeModalAction } from '../../../actions/modal';
import { UPDATE_MODAL_HEADER } from '../../../types/modal';

export const assetConfigModalSize = {
    modalWidth: 'min(80rem, calc(100vw - 2rem))',
    modalHeight: 'auto',
    maxHeight: 'min(74vh, calc(100dvh - 10rem))',
};

const getListFromResponse = (response) => {
    const data = response?.data?.data ?? response?.data ?? [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.asset_configs)) return data.asset_configs;
    if (data && typeof data === 'object') return [data];
    return [];
};

const getConfigRows = (config) => {
    const rows = configToRows(config);
    return rows.length > 0 ? rows : [emptyConfigRow()];
};

const getConfigHash = (rows = []) => JSON.stringify(buildConfigPayload(rows));

// Use saved rows so removing the last field does not hide the editor before saving.
const hasSavedConfig = (config) => Object.keys(buildConfigPayload(config?.originalRows)).length > 0;

const formatAttributeLabel = (attribute = {}) => {
    const label = attribute.name || attribute.key || '';
    return String(label)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}([T\s]|$)/.test(value);

const formatAttributeValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (isIsoDate(trimmedValue) && moment(trimmedValue).isValid()) return moment(trimmedValue).format('DD MMM YYYY');
        return trimmedValue;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value))
        return value
            .map(formatAttributeValue)
            .filter((item) => item !== '')
            .join(', ');
    if (typeof value === 'object') {
        const nestedValue = value.str ?? value.value ?? value.label ?? value.name;
        if (nestedValue === undefined || nestedValue === value) return '';
        return formatAttributeValue(nestedValue);
    }
    return '';
};

const getAssetAttributes = (config, asset) => {
    const attributes = config?.asset?.attributes || config?.attributes || asset?.attributes;
    if (!Array.isArray(attributes)) return [];
    return attributes
        .filter((attribute) => attribute && (attribute.name || attribute.key))
        .map((attribute, index) => ({
            id: attribute.id || `${attribute.key || attribute.name}-${index}`,
            label: formatAttributeLabel(attribute),
            value: formatAttributeValue(attribute.value),
        }));
};

// buildConfigPayload drops blank keys and lets a repeated key overwrite the earlier one, so warn before saving.
const getRowIssues = (rows = []) => {
    const seenKeys = new Set();
    const duplicateKeys = new Set();
    let hasOrphanValue = false;

    rows.forEach(({ key, value }) => {
        const trimmedKey = String(key || '').trim();
        if (!trimmedKey) {
            if (String(value || '').trim()) hasOrphanValue = true;
            return;
        }
        if (seenKeys.has(trimmedKey)) duplicateKeys.add(trimmedKey);
        seenKeys.add(trimmedKey);
    });

    return { duplicateKeys, hasOrphanValue };
};

const AssetAttributes = ({ attributes = [], isOpen, onToggle }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const attributesId = useId();

    const visibleAttributes = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return attributes;
        return attributes.filter(({ label, value }) => `${label} ${value}`.toLowerCase().includes(term));
    }, [attributes, searchTerm]);

    if (attributes.length === 0) return null;
    const isSearchable = attributes.length > 6;

    return (
        <section className='flex min-h-0 flex-col overflow-hidden rounded-10px border border-scogoeee bg-white shadow-sm' aria-label='Asset attributes'>
            <div className='flex flex-shrink-0 flex-wrap items-center justify-between gap-2 px-3 py-2 sm:px-4'>
                <button
                    type='button'
                    onClick={onToggle}
                    aria-expanded={isOpen}
                    aria-controls={attributesId}
                    className='flex items-center gap-2 rounded-5px text-font12 font-medium text-scogoprimary transition-colors hover:text-scogobgsky focus-visible:ring-2 focus-visible:ring-offset-2'
                >
                    <span aria-hidden='true' className={`material-icons-outlined text-font16 transition-transform ${isOpen ? '' : '-rotate-90'}`}>
                        expand_more
                    </span>
                    Asset Attributes
                    <span className='ml-1 rounded-full bg-scogolightblue px-2.5 py-0.5 text-font10 font-medium text-scogoprimary'>{attributes.length}</span>
                </button>
                {isOpen && isSearchable ? (
                    <div className='relative w-full sm:w-2/5'>
                        <span aria-hidden='true' className='material-icons-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-font14 text-scogo99'>
                            search
                        </span>
                        <input
                            type='search'
                            aria-label='Search attributes'
                            className='w-full rounded-6px border border-scogoeee bg-scogof8 py-1.5 pl-10 pr-3 text-font10 text-scogo21 outline-none transition-colors focus:border-scogoprimary focus:bg-white focus:ring-0'
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder='Search attributes'
                        />
                    </div>
                ) : null}
            </div>
            <div id={attributesId} hidden={!isOpen} className='min-h-0 flex-1 overflow-y-auto border-t border-scogoeee px-3 py-2 sm:px-4'>
                {visibleAttributes.length === 0 ? (
                    <p role='status' className='py-2 text-center text-font10 text-scogo70'>
                        No attributes match your search.
                    </p>
                ) : (
                    <dl className='grid grid-cols-[repeat(auto-fit,minmax(min(100%,15rem),1fr))] gap-2'>
                        {visibleAttributes.map(({ id, label, value }) => (
                            <div key={id} className='min-w-0 rounded-6px bg-scogof8 px-3 py-2'>
                                <dt className='break-words text-font10 font-medium text-scogo70'>{label}</dt>
                                <dd className='mt-1 whitespace-pre-wrap break-words text-font11 font-medium text-scogo21'>{value === '' ? '—' : value}</dd>
                            </div>
                        ))}
                    </dl>
                )}
            </div>
        </section>
    );
};

const inputClass =
    'w-full min-w-0 rounded-6px border bg-white px-3 py-2 text-font11 text-scogo21 outline-none transition-colors placeholder:text-scogo99 hover:border-scogoccc focus:border-scogoprimary focus:ring-0 disabled:cursor-not-allowed disabled:bg-scogof8';

const ConfigRow = ({ row, rowIndex, isDuplicate, isOnlyEmptyRow, isSaving, keyInputRef, onChange, onRemove }) => {
    return (
        <div className='grid min-w-0 grid-cols-[minmax(0,1fr)_3rem] items-center gap-x-3 gap-y-1.5 rounded-6px border border-scogoeee bg-scogof8 p-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_3rem] sm:border-0 sm:bg-white sm:px-2 sm:py-1 sm:hover:bg-scogof8'>
            <label className='min-w-0'>
                <span className='mb-1 block text-font10 font-medium text-scogo70 sm:sr-only'>
                    Key <span className='sr-only'>{rowIndex + 1}</span>
                </span>
                <input
                    ref={keyInputRef}
                    className={`${inputClass} ${isDuplicate ? 'border-scogoclosed' : 'border-scogoddd'}`}
                    aria-invalid={isDuplicate}
                    disabled={isSaving}
                    value={row.key}
                    onChange={(event) => onChange('key', event.target.value)}
                    placeholder='Enter key'
                />
            </label>
            <label className='col-start-1 row-start-2 min-w-0 sm:col-start-auto sm:row-start-auto'>
                <span className='mb-1 block text-font10 font-medium text-scogo70 sm:sr-only'>
                    Value <span className='sr-only'>{rowIndex + 1}</span>
                </span>
                <input
                    className={`${inputClass} border-scogoddd`}
                    disabled={isSaving}
                    value={row.value}
                    onChange={(event) => onChange('value', event.target.value)}
                    placeholder='Enter value'
                />
            </label>
            <div className='col-start-2 row-span-2 row-start-1 flex justify-center sm:col-start-auto sm:row-span-1 sm:row-start-auto'>
                <button
                    type='button'
                    title={isOnlyEmptyRow ? 'Nothing to remove' : 'Remove field'}
                    aria-label={`Remove field ${rowIndex + 1}`}
                    disabled={isOnlyEmptyRow || isSaving}
                    onClick={onRemove}
                    className='flex h-10 w-10 items-center justify-center rounded-6px text-scogo99 transition-colors hover:bg-scogoLightRed hover:text-scogoclosed focus-visible:ring-2 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-scogoddd'
                >
                    <span aria-hidden='true' className='material-icons-outlined text-font16'>
                        delete_outline
                    </span>
                </button>
            </div>
        </div>
    );
};

const ConfigCard = ({ config, configIndex, configOptions, isSaving, registerKeyInput, onRowChange, onAddRow, onRemoveRow, onSelectConfig }) => {
    const hasChanges = getConfigHash(config.rows) !== config.originalConfigHash;
    const { duplicateKeys } = getRowIssues(config.rows);
    const isOnlyEmptyRow = config.rows.length === 1 && !config.rows[0].key && !config.rows[0].value;
    const fieldCount = Object.keys(buildConfigPayload(config.rows)).length;

    return (
        <section
            aria-label={configOptions.length > 1 ? `Configuration ${configIndex + 1}` : 'Configuration'}
            className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-10px border bg-white shadow-sm transition-colors ${
                hasChanges ? 'border-scogoprimary' : 'border-scogoeee'
            }`}
        >
            <div className='flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b border-scogoeee bg-scogolightblue px-2 py-2'>
                <div className='flex min-w-0 items-center gap-3'>
                    <span aria-hidden='true' className='material-icons-outlined text-font18 text-scogoprimary'>
                        tune
                    </span>
                    <div>
                        {configOptions.length > 1 ? (
                            <select
                                aria-label='Select configuration'
                                value={configIndex}
                                disabled={isSaving}
                                onChange={(event) => onSelectConfig(Number(event.target.value))}
                                className='max-w-full rounded-6px border border-scogoddd bg-white py-1 pl-2 pr-8 text-font11 font-medium text-scogoprimary focus:border-scogoprimary focus:ring-0'
                            >
                                {configOptions.map(({ config: item, index }) => (
                                    <option key={item.id || index} value={index}>
                                        Configuration {index + 1}
                                        {getConfigHash(item.rows) !== item.originalConfigHash ? ' • Unsaved' : ''}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <h3 className='text-font12 font-medium text-scogoprimary'>Configuration</h3>
                        )}
                    </div>
                </div>
                <span className='flex-shrink-0 rounded-full bg-white px-3 py-1 text-font10 font-medium text-scogoprimary'>
                    {fieldCount} {fieldCount === 1 ? 'field' : 'fields'}
                </span>
            </div>

            <div className='flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-2 pt-2'>
                <div
                    aria-hidden='true'
                    className='hidden min-w-0 flex-shrink-0 grid-cols-[minmax(0,2fr)_minmax(0,3fr)_3rem] gap-3 px-2 pb-1 text-font10 font-medium uppercase tracking-wide text-scogo70 sm:grid'
                >
                    <span>Key</span>
                    <span>Value</span>
                    <span className='sr-only'>Action</span>
                </div>
                <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto py-1 sm:gap-0'>
                    {config.rows.map((row, rowIndex) => (
                        <ConfigRow
                            key={`${config.id || 'config'}-${rowIndex}`}
                            row={row}
                            rowIndex={rowIndex}
                            isDuplicate={duplicateKeys.has(String(row.key || '').trim())}
                            isOnlyEmptyRow={isOnlyEmptyRow}
                            isSaving={isSaving}
                            keyInputRef={registerKeyInput(configIndex, rowIndex)}
                            onChange={(field, value) => onRowChange({ configIndex, rowIndex, field, value })}
                            onRemove={() => onRemoveRow({ configIndex, rowIndex })}
                        />
                    ))}
                </div>
                <button
                    type='button'
                    onClick={() => onAddRow(configIndex)}
                    disabled={isSaving}
                    className='mt-2 flex w-full flex-shrink-0 items-center justify-center gap-2 rounded-6px border border-dashed border-scogoccc py-1.5 text-font10 font-medium text-scogoprimary transition-colors hover:border-scogoprimary hover:bg-scogolightblue focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50'
                >
                    <span aria-hidden='true' className='material-icons-outlined text-font14'>
                        add
                    </span>
                    Add Field
                </button>
            </div>
        </section>
    );
};

const ConfigFooter = ({ config, configIndex, isSaving, onSave, onCancel }) => {
    const hasChanges = config && getConfigHash(config.rows) !== config.originalConfigHash;
    const { duplicateKeys, hasOrphanValue } = getRowIssues(config?.rows);
    let warning = '';
    if (duplicateKeys.size > 0) warning = `Duplicate ${duplicateKeys.size > 1 ? 'keys' : 'key'}: ${Array.from(duplicateKeys).join(', ')}`;
    else if (hasOrphanValue) warning = 'Rows without a key will not be saved';

    return (
        <footer aria-label='Configuration actions' className='flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-t border-scogoeee bg-white px-3 py-2 sm:px-4'>
            <span role='status' className={`flex min-w-0 items-center gap-2 text-font10 ${duplicateKeys.size > 0 ? 'text-scogoclosed' : 'text-scogo70'}`}>
                {warning ? (
                    <>
                        <span aria-hidden='true' className='material-icons-outlined flex-shrink-0 text-font14'>
                            error_outline
                        </span>
                        <span className='truncate' title={warning}>
                            {warning}
                        </span>
                    </>
                ) : hasChanges ? (
                    <>
                        <span aria-hidden='true' className='h-2 w-2 rounded-full bg-scogoorange' />
                        Unsaved changes
                    </>
                ) : null}
            </span>
            <div className='ml-auto flex flex-shrink-0 items-center gap-2'>
                {config && (
                    <ButtonScogoPrimary
                        type='button'
                        textOrComponent='Save'
                        loading={isSaving}
                        disabled={!hasChanges || duplicateKeys.size > 0}
                        onHoverTitle={duplicateKeys.size > 0 ? 'Resolve duplicate keys to save' : ''}
                        onClick={() => onSave(configIndex)}
                    />
                )}
                <ButtonScogoClosedOutlined type='button' textOrComponent='Cancel' disabled={isSaving} onClick={onCancel} />
            </div>
        </footer>
    );
};

const ConfigSkeleton = () => (
    <div role='status' aria-label='Loading asset configuration' className='flex h-full flex-col gap-5 bg-scogof8 p-4 sm:p-6'>
        <span className='sr-only'>Loading asset configuration…</span>
        <div className='h-12 w-1/3 animate-pulse rounded-6px bg-scogoddd' />
        <div className='flex flex-col gap-4 rounded-10px border border-scogoeee bg-white p-5'>
            <div className='mb-2 h-12 animate-pulse rounded-6px bg-scogolightblue' />
            {[0, 1, 2].map((item) => (
                <div key={item} className='h-12 animate-pulse rounded-6px bg-scogof2' />
            ))}
        </div>
    </div>
);

const EmptyConfig = () => (
    <div role='status' className='flex items-center gap-3 rounded-6px border border-scogoeee bg-white px-4 py-3 text-font11 text-scogo70'>
        <span aria-hidden='true' className='material-icons-outlined text-font16 text-scogo99'>
            info
        </span>
        <p>No asset config found</p>
    </div>
);

export default function ViewAssetConfig({ ticket, assetId, asset }) {
    const dispatch = useDispatch();
    const [configs, setConfigs] = useState([]);
    const [activeConfigIndex, setActiveConfigIndex] = useState(0);
    const [attributesOpen, setAttributesOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [savingConfigId, setSavingConfigId] = useState('');
    const [focusTarget, setFocusTarget] = useState('');
    const keyInputRefs = useRef({});
    const activeConfig = configs[activeConfigIndex];
    const configOptions = configs.map((config, index) => ({ config, index })).filter(({ config }) => hasSavedConfig(config));
    const assetNumber = activeConfig?.asset?.asset_number || activeConfig?.asset?.external_id || asset?.asset_number || asset?.external_id;

    useEffect(() => {
        dispatch({
            type: UPDATE_MODAL_HEADER,
            payload: <span className='text-font16'>View/Edit Config {assetNumber && <span className='text-scogoprimary'>| {assetNumber}</span>}</span>,
        });
    }, [assetNumber, dispatch]);

    const hydrateConfigs = (list = []) => {
        return list.map((item) => {
            const rows = getConfigRows(item.config);
            return {
                ...item,
                rows,
                originalRows: rows,
                originalConfigHash: getConfigHash(rows),
            };
        });
    };

    useEffect(() => {
        const getConfigs = async () => {
            setIsLoading(true);
            try {
                const query = assetId ? { asset_id: assetId } : { ticket_id: ticket?.id };
                const response = await scogo_apis.assetConfig.getAssetConfigs({}, {}, query);
                const nextConfigs = hydrateConfigs(getListFromResponse(response));
                setConfigs(nextConfigs);
                setActiveConfigIndex(Math.max(0, nextConfigs.findIndex(hasSavedConfig)));
                setAttributesOpen(true);
            } catch (err) {
                dispatch(sendToastMessage({ status: 'danger', message: err?.response?.data?.messages?.[0] || 'Unable to fetch asset config' }));
                setConfigs([]);
            } finally {
                setIsLoading(false);
            }
        };

        if (ticket?.id || assetId) getConfigs();
    }, [assetId, dispatch, ticket?.id]);

    useEffect(() => {
        if (!focusTarget) return;
        keyInputRefs.current[focusTarget]?.focus();
        setFocusTarget('');
    }, [focusTarget, configs]);

    const registerKeyInput = (configIndex, rowIndex) => (element) => {
        keyInputRefs.current[`${configIndex}-${rowIndex}`] = element;
    };

    const updateRow = ({ configIndex, rowIndex, field, value }) => {
        setConfigs((prev) =>
            prev.map((config, currentConfigIndex) => {
                if (currentConfigIndex !== configIndex) return config;
                const rows = config.rows.map((row, currentRowIndex) => {
                    if (currentRowIndex !== rowIndex) return row;
                    return { ...row, [field]: value };
                });
                return { ...config, rows };
            })
        );
    };

    const addRow = (configIndex) => {
        const rowIndex = configs[configIndex]?.rows?.length ?? 0;
        setConfigs((prev) =>
            prev.map((config, currentConfigIndex) => {
                if (currentConfigIndex !== configIndex) return config;
                return { ...config, rows: [...config.rows, emptyConfigRow()] };
            })
        );
        setFocusTarget(`${configIndex}-${rowIndex}`);
    };

    const removeRow = ({ configIndex, rowIndex }) => {
        setConfigs((prev) =>
            prev.map((config, currentConfigIndex) => {
                if (currentConfigIndex !== configIndex) return config;
                if (config.rows.length <= 1) return { ...config, rows: [emptyConfigRow()] };
                return {
                    ...config,
                    rows: config.rows.filter((_, currentRowIndex) => currentRowIndex !== rowIndex),
                };
            })
        );
    };

    const saveConfig = async (configIndex) => {
        const selectedConfig = configs[configIndex];
        if (!selectedConfig?.id) return;
        if (getConfigHash(selectedConfig.rows) === selectedConfig.originalConfigHash) return;

        setSavingConfigId(selectedConfig.id);
        try {
            const payload = { config: buildConfigPayload(selectedConfig.rows) };
            const response = await scogo_apis.assetConfig.updateAssetConfig({}, { id: selectedConfig.id }, {}, payload);
            const updatedConfig = response?.data?.data || response?.data || { ...selectedConfig, config: payload.config };
            setConfigs((prev) =>
                prev.map((item, currentConfigIndex) => {
                    if (currentConfigIndex !== configIndex) return item;
                    const rows = getConfigRows(updatedConfig.config || payload.config);
                    return { ...item, ...updatedConfig, rows, originalRows: rows, originalConfigHash: getConfigHash(rows) };
                })
            );
            dispatch(sendToastMessage({ status: 'success', message: 'Asset config updated successfully' }));
            dispatch(closeModalAction());
        } catch (err) {
            dispatch(sendToastMessage({ status: 'danger', message: err?.response?.data?.messages?.[0] || 'Unable to update asset config' }));
        } finally {
            setSavingConfigId('');
        }
    };

    const attributes = useMemo(() => getAssetAttributes(activeConfig, asset), [activeConfig, asset]);
    const hasAttributes = attributes.length > 0;
    const hasConfig = hasSavedConfig(activeConfig);
    const cardRows = hasAttributes && hasConfig ? (attributesOpen ? 'grid-rows-2' : 'grid-rows-[auto_minmax(0,1fr)]') : 'grid-rows-1';

    if (isLoading) return <ConfigSkeleton />;

    return (
        <div
            className='flex min-h-0 flex-col overflow-hidden bg-scogof8'
            style={{ maxHeight: assetConfigModalSize.maxHeight, height: hasConfig ? assetConfigModalSize.maxHeight : undefined }}
        >
            <div className={`grid min-h-0 flex-1 gap-3 overflow-hidden p-3 sm:px-4 ${cardRows}`}>
                {hasAttributes && (
                    <AssetAttributes
                        key={`attributes-${activeConfig?.id || 'asset'}`}
                        attributes={attributes}
                        isOpen={attributesOpen}
                        onToggle={() => setAttributesOpen((previous) => !previous)}
                    />
                )}
                {!hasConfig && !hasAttributes && <EmptyConfig />}
                {hasConfig && (
                    <ConfigCard
                        key={activeConfig.id || activeConfigIndex}
                        config={activeConfig}
                        configIndex={activeConfigIndex}
                        configOptions={configOptions}
                        isSaving={savingConfigId !== ''}
                        registerKeyInput={registerKeyInput}
                        onRowChange={updateRow}
                        onAddRow={addRow}
                        onRemoveRow={removeRow}
                        onSelectConfig={(index) => {
                            setActiveConfigIndex(index);
                            setAttributesOpen(true);
                        }}
                    />
                )}
            </div>
            <ConfigFooter
                config={hasConfig ? activeConfig : undefined}
                configIndex={activeConfigIndex}
                isSaving={savingConfigId !== ''}
                onSave={saveConfig}
                onCancel={() => dispatch(closeModalAction())}
            />
        </div>
    );
}
